// OAuth return (§14, §6.2). Resumes pendingInvite: no profile -> onboarding
// (invite context preserved in localStorage); profiled -> create_blend -> blend.
//
// IMPORTANT (iOS Safari): after an OAuth redirect the session may still be
// mid-exchange (detectSessionInUrl / PKCE) when auth.loading first flips false.
// Bouncing to "/" on a null user at that instant is the "back to main page" bug.
// So when the URL looks like an OAuth return, we WAIT a grace period for the
// session to settle before giving up. Every step is logged to client_events so
// the funnel is visible for cases we can't reproduce.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page } from "../components/Layout.tsx";
import { useAuth, consumePendingInvite } from "../auth/AuthProvider.tsx";
import { getMyProfile, createBlend } from "../lib/api.ts";
import { track, clientHints } from "../lib/analytics.ts";
import { decideCallbackAction, isOAuthReturn } from "../lib/authCallbackDecision.ts";

const GRACE_MS = 8000;

export default function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("Signing you in…");
  const [gaveUp, setGaveUp] = useState(false);
  const handledRef = useRef(false);

  // Log entry once, capturing what the return URL looks like.
  useEffect(() => {
    const href = typeof window !== "undefined" ? window.location.href : "";
    track("callback_entered", {
      has_code: /[?&]code=/.test(href),
      has_hash_token: /access_token=/.test(href),
      has_error: /[?&]error=/.test(href),
      ...clientHints(),
    });
  }, []);

  useEffect(() => {
    if (handledRef.current) return;
    const href = typeof window !== "undefined" ? window.location.href : "";
    const action = decideCallbackAction({
      loading: auth.loading,
      hasUser: !!auth.user,
      href,
      gaveUp,
    });

    if (action === "wait") {
      if (auth.loading) return;
      // Waiting specifically for the OAuth exchange: arm the grace-period timer.
      setMsg("Finishing sign-in…");
      track("callback_awaiting_session", clientHints());
      const t = setTimeout(() => setGaveUp(true), GRACE_MS);
      return () => clearTimeout(t);
    }

    if (action === "bounce") {
      handledRef.current = true;
      track("callback_no_session_bounce", { oauth_return: isOAuthReturn(href), gave_up: gaveUp, ...clientHints() });
      navigate("/", { replace: true });
      return;
    }

    // action === "route": signed in.
    {
      handledRef.current = true;
      track("callback_session_ok");
      (async () => {
        try {
          const profile = await getMyProfile().catch(() => null);
          if (!profile) {
            navigate("/onboarding", { replace: true });
            return;
          }
          const pending = consumePendingInvite();
          if (pending) {
            setMsg("Setting up your blend…");
            const res = await createBlend(pending).catch(() => null);
            if (res?.self) {
              navigate("/me?toast=self-invite", { replace: true });
              return;
            }
            if (res?.blend_slug) {
              navigate(`/b/${res.blend_slug}`, { replace: true });
              return;
            }
          }
          navigate("/me", { replace: true });
        } catch (e) {
          track("callback_routing_error", { error: String((e as Error)?.message ?? e) });
          navigate("/me", { replace: true });
        }
      })();
      return;
    }
  }, [auth.loading, auth.user, gaveUp, navigate]);

  return (
    <Page testid="screen-auth-callback">
      <p className="text-center text-ink-400">{msg}</p>
    </Page>
  );
}
